import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Clock, MessageSquare, Settings, AlertTriangle, CheckCircle, Brain, Target, Timer, Calendar, Zap, AlertCircle, PlayCircle, Square, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card/Card';
import { Separator } from '../../../components/ui/separator';
import { Input } from '../../../components/ui/Input/Input';
import { AutoExpandingTextarea } from '../../../components/ui/AutoExpandingTextarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Label } from '../../../components/ui/label';
import { cn } from '../../../utils/cn';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/Toast';

export interface AutomationConfig {
  id?: string;
  conversationId: string;
  isEnabled: boolean;
  activationType: 'immediate' | 'on_response'; // New field for activation type
  
  // Response Instructions
  instructions: string;
  goal: string;
  tone: 'professional' | 'friendly' | 'casual' | 'empathetic' | 'custom';
  customTone?: string;
  
  // Stop Conditions
  stopConditions: {
    maxResponses: number;
    keywords: string[];
    requiresHumanKeywords: string[];
    sentimentThreshold: 'negative' | 'very_negative' | 'any';
    complexityThreshold: 'medium' | 'high' | 'very_high';
  };
  
  // Timing Settings
  timing: {
    activeHours: {
      start: string; // HH:MM format
      end: string; // HH:MM format
      timezone: string;
      weekdays: number[]; // 0-6, Sunday = 0
    };
    delayRange: {
      min: number; // minutes
      max: number; // minutes
    };
  };
  
  // Advanced Settings
  advanced: {
    contextWindow: number; // number of previous messages to consider
    confidenceThreshold: number; // 0-1
    escalateOnUncertainty: boolean;
    allowFollowUp: boolean;
  };
}

// Add status type
type AutomationStatus = 'not_configured' | 'active' | 'dormant' | 'stopped' | 'error';

interface AutomationModalProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: AutomationConfig, activateNow?: boolean) => void;
  existingConfig?: AutomationConfig | null;
}

export const AutomationModal: React.FC<AutomationModalProps> = ({ 
  conversationId, 
  isOpen, 
  onClose, 
  onSave, 
  existingConfig 
}) => {
  const { addToast } = useToast();
  // Default configuration - shared between initial config and reset
  const getDefaultConfig = (): AutomationConfig => ({
    conversationId,
    isEnabled: false,
    activationType: 'immediate',
    instructions: '',
    goal: '',
    tone: 'professional',
    stopConditions: {
      maxResponses: 5,
      keywords: [],
      requiresHumanKeywords: ['urgent', 'complaint', 'cancel', 'refund', 'angry', 'frustrated'],
      sentimentThreshold: 'negative',
      complexityThreshold: 'high'
    },
    timing: {
      activeHours: {
        start: '09:00',
        end: '18:00',
        timezone: 'America/Los_Angeles',
        weekdays: [1, 2, 3, 4, 5] // Monday to Friday
      },
      delayRange: {
        min: 9,
        max: 14
      }
    },
    advanced: {
      contextWindow: 10,
      confidenceThreshold: 0.8,
      escalateOnUncertainty: true,
      allowFollowUp: true
    }
  });

  // Load config from existing config or use defaults
  const getInitialConfig = (): AutomationConfig => {
    const defaultConfig = getDefaultConfig();

    // Helper function to deep merge configs with defaults
    const mergeWithDefaults = (config: Partial<AutomationConfig>): AutomationConfig => {
      return {
        ...defaultConfig,
        ...config,
        stopConditions: {
          ...defaultConfig.stopConditions,
          ...(config.stopConditions || {})
        },
        timing: {
          ...defaultConfig.timing,
          ...(config.timing || {}),
          activeHours: {
            ...defaultConfig.timing.activeHours,
            ...(config.timing?.activeHours || {})
          },
          delayRange: {
            ...defaultConfig.timing.delayRange,
            ...(config.timing?.delayRange || {})
          }
        },
        advanced: {
          ...defaultConfig.advanced,
          ...(config.advanced || {})
        }
      };
    };

    // Use existing config from props (loaded from database), merging with defaults
    if (existingConfig) {
      return mergeWithDefaults(existingConfig);
    }

    // Return default configuration
    return defaultConfig;
  };

  const [config, setConfig] = useState<AutomationConfig>(getInitialConfig());

  const [activeTab, setActiveTab] = useState<'basic' | 'timing' | 'conditions' | 'advanced'>('basic');
  const [showStartConfirmation, setShowStartConfirmation] = useState(false);
  const [showStopConfirmation, setShowStopConfirmation] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Auto-save functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Update config when existingConfig prop changes
  useEffect(() => {
    if (isOpen && existingConfig) {
      console.log('📥 Loading existing automation config:', existingConfig);
      const mergedConfig = getInitialConfig(); // This already handles merging with existingConfig
      setConfig(mergedConfig);
      setHasUnsavedChanges(false);
    } else if (isOpen && !existingConfig) {
      console.log('🆕 No existing config, using defaults');
      setConfig(getDefaultConfig());
      setHasUnsavedChanges(false);
    }
  }, [isOpen, existingConfig, conversationId]);

  // Auto-save function
  const autoSaveConfig = useCallback(async (configToSave: AutomationConfig) => {
    if (!configToSave.instructions || !configToSave.goal) {
      // Don't auto-save incomplete configurations
      return;
    }

    try {
      setIsAutoSaving(true);
      console.log('🔄 Auto-saving automation config...');
      
      // Save without activating
      await onSave({ ...configToSave, isEnabled: false }, false);
      setHasUnsavedChanges(false);
      
      console.log('✅ Auto-save completed');
    } catch (error) {
      console.warn('⚠️ Auto-save failed:', error);
      // Don't show error to user for auto-save failures
    } finally {
      setIsAutoSaving(false);
    }
  }, [onSave]);

  // Track config changes for auto-save
  useEffect(() => {
    if (existingConfig && JSON.stringify(config) !== JSON.stringify(existingConfig)) {
      setHasUnsavedChanges(true);
    }
  }, [config, existingConfig]);

  // Auto-save when modal is closed with unsaved changes
  const handleClose = useCallback(async () => {
    if (hasUnsavedChanges && config.instructions && config.goal) {
      console.log('💾 Auto-saving before closing modal...');
      try {
        await autoSaveConfig(config);
      } catch (error) {
        console.warn('⚠️ Failed to auto-save before closing:', error);
      }
    }
    onClose();
  }, [hasUnsavedChanges, config, autoSaveConfig, onClose]);

  // Reset configuration to defaults
  const handleResetConfiguration = () => {
    setShowResetConfirmation(true);
  };

  const confirmResetConfiguration = () => {
    // Reset to pure default configuration
    setConfig(getDefaultConfig());
    setShowResetConfirmation(false);
  };

  if (!isOpen) return null;

  // Save configuration without activating
  const handleSaveConfiguration = async () => {
    try {
      await onSave({ ...config, isEnabled: false }, false);
      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  };

  // Save and activate based on activation type
  const handleSaveAndActivate = async () => {
    try {
      if (config.activationType === 'immediate') {
        await onSave({ ...config, isEnabled: true }, true);
      } else {
        await onSave({ ...config, isEnabled: true }, false);
      }
      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      console.error('Failed to save and activate:', error);
    }
  };

  const weekdays = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  const tones = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-appropriate' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and personable' },
    { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
    { value: 'empathetic', label: 'Empathetic', description: 'Understanding and supportive' },
    { value: 'custom', label: 'Custom', description: 'Define your own tone' }
  ];

  // Determine automation status
  const getAutomationStatus = (): AutomationStatus => {
    if (!config.instructions || !config.goal) {
      return 'not_configured';
    }
    if (config.isEnabled) {
      return 'active';
    }
    return 'dormant';
  };

  const status = getAutomationStatus();

  // Status display configuration
  const statusConfig = {
    not_configured: {
      label: 'Not yet configured',
      icon: AlertCircle,
      className: 'text-muted-foreground',
      bgClassName: 'bg-muted',
    },
    active: {
      label: 'Active',
      icon: PlayCircle,
      className: 'text-green-600',
      bgClassName: 'bg-green-50',
    },
    dormant: {
      label: 'Dormant',
      icon: Clock,
      className: 'text-yellow-600',
      bgClassName: 'bg-yellow-50',
    },
    stopped: {
      label: 'Stopped',
      icon: Square,
      className: 'text-red-600',
      bgClassName: 'bg-red-50',
    },
    error: {
      label: 'Error',
      icon: AlertCircle,
      className: 'text-red-600',
      bgClassName: 'bg-red-50',
    },
  };

  const StatusIcon = statusConfig[status].icon;

  // Handle start automation with confirmation
  const handleStartAutomation = () => {
    if (status === 'not_configured') {
      // If not configured, focus on first required field
      document.getElementById('goal')?.focus();
      return;
    }
    setShowStartConfirmation(true);
  };

  const confirmStartAutomation = async () => {
    try {
      // Auto-save config before activating
      if (hasUnsavedChanges) {
        await autoSaveConfig(config);
      }
      
      if (config.activationType === 'immediate') {
        await onSave({ ...config, isEnabled: true }, true);
      } else {
        await onSave({ ...config, isEnabled: true }, false);
      }
      setConfig(prev => ({ ...prev, isEnabled: true }));
      setHasUnsavedChanges(false);
      setShowStartConfirmation(false);
      
      // Show success toast
      addToast({
        title: 'Automation Activated',
        message: config.activationType === 'immediate' 
          ? 'AI will start responding immediately to messages'
          : 'AI will start responding when the next message arrives',
        type: 'success'
      });
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to start automation:', error);
      addToast({
        title: 'Activation Failed',
        message: 'Failed to activate automation. Please try again.',
        type: 'error'
      });
    }
  };

  // Handle stop automation with confirmation
  const handleStopAutomation = () => {
    setShowStopConfirmation(true);
  };

  const confirmStopAutomation = async () => {
    try {
      // Auto-save config before stopping
      if (hasUnsavedChanges) {
        await autoSaveConfig(config);
      }
      
      const updatedConfig = { ...config, isEnabled: false };
      await onSave(updatedConfig, false);
      setConfig(updatedConfig);
      setHasUnsavedChanges(false);
      setShowStopConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Failed to stop automation:', error);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Automated Responses
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure AI to automatically respond to this conversation
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Status Display */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig[status].bgClassName}`}>
                <StatusIcon className={`h-4 w-4 ${statusConfig[status].className}`} />
                <span className={`text-sm font-medium ${statusConfig[status].className}`}>
                  {statusConfig[status].label}
                </span>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex overflow-x-auto">
              {[
                { id: 'basic', label: 'Instructions', icon: MessageSquare },
                { id: 'timing', label: 'Timing', icon: Clock },
                { id: 'conditions', label: 'Stop Conditions', icon: AlertTriangle },
                { id: 'advanced', label: 'Advanced', icon: Settings }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                    activeTab === tab.id
                      ? "border-purple-500 text-purple-600 dark:text-purple-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Goal Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-blue-600" />
                      Automation Goal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="goal" className="text-sm font-medium">
                        What should the AI achieve?
                      </Label>
                      <AutoExpandingTextarea
                        id="goal"
                        placeholder="e.g., Help customers with product questions, schedule demos, provide support..."
                        value={config.goal}
                        onChange={(e) => setConfig(prev => ({ ...prev, goal: e.target.value }))}
                        minRows={2}
                        maxRows={4}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Instructions Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4 text-purple-600" />
                      AI Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="instructions" className="text-sm font-medium">
                        Detailed instructions for the AI
                      </Label>
                      <AutoExpandingTextarea
                        id="instructions"
                        placeholder="Provide specific instructions on how the AI should respond, what information to include, and how to handle different scenarios..."
                        value={config.instructions}
                        onChange={(e) => setConfig(prev => ({ ...prev, instructions: e.target.value }))}
                        minRows={4}
                        maxRows={8}
                        className="mt-1"
                      />
                    </div>

                    {/* Tone Selection */}
                    <div>
                      <Label className="text-sm font-medium">Response Tone</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {tones.map((tone) => (
                          <div key={tone.value} className="relative">
                            <input
                              type="radio"
                              id={`tone-${tone.value}`}
                              name="tone"
                              value={tone.value}
                              checked={config.tone === tone.value}
                              onChange={(e) => setConfig(prev => ({ 
                                ...prev, 
                                tone: e.target.value as any 
                              }))}
                              className="sr-only"
                            />
                            <label
                              htmlFor={`tone-${tone.value}`}
                              className={cn(
                                "block p-3 rounded-lg border cursor-pointer transition-colors",
                                config.tone === tone.value
                                  ? "border-purple-500 bg-purple-50 dark:bg-purple-950/30"
                                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                              )}
                            >
                              <div className="font-medium text-sm">{tone.label}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {tone.description}
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                      
                      {config.tone === 'custom' && (
                        <div className="mt-3">
                          <Input
                            placeholder="Describe your custom tone..."
                            value={config.customTone || ''}
                            onChange={(e) => setConfig(prev => ({ 
                              ...prev, 
                              customTone: e.target.value 
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Activation Type Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4 text-green-600" />
                      Activation Mode
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">When should automation start?</Label>
                      <div className="grid grid-cols-1 gap-3 mt-2">
                        <div className="relative">
                          <input
                            type="radio"
                            id="activation-immediate"
                            name="activationType"
                            value="immediate"
                            checked={config.activationType === 'immediate'}
                            onChange={(e) => setConfig(prev => ({ 
                              ...prev, 
                              activationType: e.target.value as 'immediate' | 'on_response'
                            }))}
                            className="sr-only"
                          />
                          <label
                            htmlFor="activation-immediate"
                            className={cn(
                              "block p-3 rounded-lg border cursor-pointer transition-colors",
                              config.activationType === 'immediate'
                                ? "border-green-500 bg-green-50 dark:bg-green-950/30"
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <PlayCircle className="h-4 w-4 text-green-600" />
                              <div>
                                <div className="font-medium text-sm">Activate Now</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Start responding immediately to recent messages
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                        
                        <div className="relative">
                          <input
                            type="radio"
                            id="activation-on-response"
                            name="activationType"
                            value="on_response"
                            checked={config.activationType === 'on_response'}
                            onChange={(e) => setConfig(prev => ({ 
                              ...prev, 
                              activationType: e.target.value as 'immediate' | 'on_response'
                            }))}
                            className="sr-only"
                          />
                          <label
                            htmlFor="activation-on-response"
                            className={cn(
                              "block p-3 rounded-lg border cursor-pointer transition-colors",
                              config.activationType === 'on_response'
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <Clock className="h-4 w-4 text-blue-600" />
                              <div>
                                <div className="font-medium text-sm">Activate on Next Response</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Wait for the next incoming message before starting automation
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'timing' && (
              <div className="space-y-6">
                {/* Active Hours */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      Active Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="start-time" className="text-sm font-medium">
                          Start Time
                        </Label>
                        <Input
                          id="start-time"
                          type="time"
                          value={config.timing.activeHours.start}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            timing: {
                              ...prev.timing,
                              activeHours: { ...prev.timing.activeHours, start: e.target.value }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="end-time" className="text-sm font-medium">
                          End Time
                        </Label>
                        <Input
                          id="end-time"
                          type="time"
                          value={config.timing.activeHours.end}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            timing: {
                              ...prev.timing,
                              activeHours: { ...prev.timing.activeHours, end: e.target.value }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="timezone" className="text-sm font-medium">
                          Timezone
                        </Label>
                        <Select
                          value={config.timing.activeHours.timezone}
                          onValueChange={(value) => setConfig(prev => ({
                            ...prev,
                            timing: {
                              ...prev.timing,
                              activeHours: { ...prev.timing.activeHours, timezone: value }
                            }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                            <SelectItem value="America/Denver">Mountain Time</SelectItem>
                            <SelectItem value="America/Chicago">Central Time</SelectItem>
                            <SelectItem value="America/New_York">Eastern Time</SelectItem>
                            <SelectItem value="UTC">UTC</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Weekdays */}
                    <div>
                      <Label className="text-sm font-medium">Active Days</Label>
                      <div className="flex gap-2 mt-2">
                        {weekdays.map((day) => (
                          <button
                            key={day.value}
                            onClick={() => {
                              const weekdays = config.timing.activeHours.weekdays;
                              const isSelected = weekdays.includes(day.value);
                              setConfig(prev => ({
                                ...prev,
                                timing: {
                                  ...prev.timing,
                                  activeHours: {
                                    ...prev.timing.activeHours,
                                    weekdays: isSelected
                                      ? weekdays.filter(d => d !== day.value)
                                      : [...weekdays, day.value].sort()
                                  }
                                }
                              }));
                            }}
                            className={cn(
                              "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                              config.timing.activeHours.weekdays.includes(day.value)
                                ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                            )}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Response Delay */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Timer className="h-4 w-4 text-orange-600" />
                      Response Delay
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="min-delay" className="text-sm font-medium">
                          Minimum Delay (minutes)
                        </Label>
                        <Input
                          id="min-delay"
                          type="number"
                          min="1"
                          max="60"
                          value={config.timing.delayRange.min}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            timing: {
                              ...prev.timing,
                              delayRange: { 
                                ...prev.timing.delayRange, 
                                min: parseInt(e.target.value) || 1 
                              }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="max-delay" className="text-sm font-medium">
                          Maximum Delay (minutes)
                        </Label>
                        <Input
                          id="max-delay"
                          type="number"
                          min="1"
                          max="60"
                          value={config.timing.delayRange.max}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            timing: {
                              ...prev.timing,
                              delayRange: { 
                                ...prev.timing.delayRange, 
                                max: parseInt(e.target.value) || 15 
                              }
                            }
                          }))}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        AI will wait a random time between {config.timing.delayRange.min} and {config.timing.delayRange.max} minutes before responding to appear more natural.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'conditions' && (
              <div className="space-y-6">
                {/* Stop Conditions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      When to Stop Automation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="max-responses" className="text-sm font-medium">
                        Maximum Responses
                      </Label>
                      <Input
                        id="max-responses"
                        type="number"
                        min="1"
                        max="20"
                        value={config.stopConditions.maxResponses}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          stopConditions: {
                            ...prev.stopConditions,
                            maxResponses: parseInt(e.target.value) || 5
                          }
                        }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Stop automation after this many AI responses
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="human-keywords" className="text-sm font-medium">
                        Keywords that require human attention
                      </Label>
                      <AutoExpandingTextarea
                        id="human-keywords"
                        placeholder="urgent, complaint, cancel, refund, angry, frustrated..."
                        value={config.stopConditions.requiresHumanKeywords.join(', ')}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          stopConditions: {
                            ...prev.stopConditions,
                            requiresHumanKeywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean)
                          }
                        }))}
                        minRows={2}
                        maxRows={4}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Comma-separated keywords that will escalate to human
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Sentiment Threshold</Label>
                        <Select
                          value={config.stopConditions.sentimentThreshold}
                          onValueChange={(value: any) => setConfig(prev => ({
                            ...prev,
                            stopConditions: {
                              ...prev.stopConditions,
                              sentimentThreshold: value
                            }
                          }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="negative">Stop on negative sentiment</SelectItem>
                            <SelectItem value="very_negative">Stop on very negative sentiment</SelectItem>
                            <SelectItem value="any">Continue regardless of sentiment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Complexity Threshold</Label>
                        <Select
                          value={config.stopConditions.complexityThreshold}
                          onValueChange={(value: any) => setConfig(prev => ({
                            ...prev,
                            stopConditions: {
                              ...prev.stopConditions,
                              complexityThreshold: value
                            }
                          }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="medium">Stop on medium complexity</SelectItem>
                            <SelectItem value="high">Stop on high complexity</SelectItem>
                            <SelectItem value="very_high">Stop on very high complexity</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings className="h-4 w-4 text-gray-600" />
                      Advanced Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="context-window" className="text-sm font-medium">
                          Context Window (messages)
                        </Label>
                        <Input
                          id="context-window"
                          type="number"
                          min="1"
                          max="50"
                          value={config.advanced.contextWindow}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            advanced: {
                              ...prev.advanced,
                              contextWindow: parseInt(e.target.value) || 10
                            }
                          }))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Number of previous messages to consider
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="confidence-threshold" className="text-sm font-medium">
                          Confidence Threshold
                        </Label>
                        <Input
                          id="confidence-threshold"
                          type="number"
                          min="0"
                          max="1"
                          step="0.1"
                          value={config.advanced.confidenceThreshold}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            advanced: {
                              ...prev.advanced,
                              confidenceThreshold: parseFloat(e.target.value) || 0.8
                            }
                          }))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Minimum confidence to send response (0-1)
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Escalate on Uncertainty</Label>
                          <p className="text-xs text-gray-500">
                            Hand off to human when AI is uncertain
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.advanced.escalateOnUncertainty}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            advanced: {
                              ...prev.advanced,
                              escalateOnUncertainty: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Allow Follow-up Questions</Label>
                          <p className="text-xs text-gray-500">
                            Let AI ask clarifying questions
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.advanced.allowFollowUp}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            advanced: {
                              ...prev.advanced,
                              allowFollowUp: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Coming Soon Features */}
                <Card className="opacity-60">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4 text-yellow-600" />
                      Coming Soon
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p>• <strong>Integration Settings:</strong> Coming soon</p>
                      <p>• <strong>Custom Models:</strong> Coming soon</p>
                      <p>• <strong>A/B Testing:</strong> Coming soon</p>
                      <p>• <strong>Performance Analytics:</strong> Coming soon</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                {isAutoSaving ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    <span>Auto-saving...</span>
                  </>
                ) : hasUnsavedChanges ? (
                  <>
                    <div className="h-2 w-2 bg-yellow-500 rounded-full" />
                    <span>Unsaved changes - will save on close</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    <span>All changes saved</span>
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                
                {/* Reset Configuration button */}
                <Button 
                  onClick={handleResetConfiguration}
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                
                {/* Save Configuration button (always available) */}
                <Button 
                  onClick={handleSaveConfiguration}
                  disabled={status === 'not_configured'}
                  variant="outline"
                  className="min-w-[140px]"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Save Configuration
                </Button>
                
                {status === 'active' ? (
                  <Button 
                    onClick={handleStopAutomation}
                    variant="destructive"
                    className="min-w-[120px]"
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Stop Automation
                  </Button>
                ) : (
                  <Button 
                    onClick={handleStartAutomation}
                    disabled={status === 'not_configured'}
                    className={cn(
                      "min-w-[140px]",
                      config.activationType === 'immediate' 
                        ? "bg-green-600 hover:bg-green-700" 
                        : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {config.activationType === 'immediate' ? (
                      <PlayCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    {status === 'not_configured' 
                      ? 'Configure First' 
                      : existingConfig?.isEnabled
                        ? (config.activationType === 'immediate'
                          ? 'Cancel & Restart Now'
                          : 'Cancel & Restart on Response')
                        : (config.activationType === 'immediate'
                          ? 'Activate Now'
                          : 'Activate on Response')
                    }
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Start Confirmation Dialog */}
      <Dialog open={showStartConfirmation} onOpenChange={setShowStartConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {config.activationType === 'immediate' ? (
                <PlayCircle className="h-5 w-5 text-green-600" />
              ) : (
                <Clock className="h-5 w-5 text-blue-600" />
              )}
              {config.activationType === 'immediate' ? 'Activate Now?' : 'Activate on Response?'}
            </DialogTitle>
            <DialogDescription>
              {config.activationType === 'immediate' 
                ? 'This will activate automated AI responses immediately for this conversation.'
                : 'This will prepare automation to start when the next message is received.'
              } The AI will:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 text-sm">
            {config.activationType === 'immediate' && (
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0" />
                <span>Process the most recent message and respond immediately</span>
              </div>
            )}
            <div className="flex items-start gap-2">
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                config.activationType === 'immediate' ? 'bg-green-600' : 'bg-blue-600'
              }`} />
              <span>
                {config.activationType === 'immediate' 
                  ? 'Respond to new messages automatically'
                  : 'Start responding when the next message arrives'
                }
              </span>
            </div>
            <div className="flex items-start gap-2">
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                config.activationType === 'immediate' ? 'bg-green-600' : 'bg-blue-600'
              }`} />
              <span>Follow your configured instructions and timing</span>
            </div>
            <div className="flex items-start gap-2">
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                config.activationType === 'immediate' ? 'bg-green-600' : 'bg-blue-600'
              }`} />
              <span>Stop when conditions are met or manually stopped</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowStartConfirmation(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmStartAutomation} 
              className={config.activationType === 'immediate' 
                ? "bg-green-600 hover:bg-green-700" 
                : "bg-blue-600 hover:bg-blue-700"
              }
            >
              {config.activationType === 'immediate' ? (
                <PlayCircle className="h-4 w-4 mr-2" />
              ) : (
                <Clock className="h-4 w-4 mr-2" />
              )}
              {config.activationType === 'immediate' ? 'Activate Now' : 'Activate on Response'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stop Confirmation Dialog */}
      <Dialog open={showStopConfirmation} onOpenChange={setShowStopConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="h-5 w-5 text-red-600" />
              Stop Automation?
            </DialogTitle>
            <DialogDescription>
              This will immediately stop all automated responses for this conversation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Note:</strong> Any scheduled responses will be cancelled. 
                You can restart automation at any time.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowStopConfirmation(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStopAutomation} variant="destructive">
              <Square className="h-4 w-4 mr-2" />
              Stop Automation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Configuration Dialog */}
      <Dialog open={showResetConfirmation} onOpenChange={setShowResetConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Reset Configuration?
            </DialogTitle>
            <DialogDescription>
              This will reset all automation settings to their default values.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Warning:</strong> This will clear all your current settings including:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>AI instructions and goals</li>
                  <li>Timing and activation settings</li>
                  <li>Stop conditions and advanced options</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowResetConfirmation(false)}>
              Cancel
            </Button>
            <Button onClick={confirmResetConfiguration} variant="destructive">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}; 